'use client';

import { useBreakpoints } from '../../../lib/hooks/useBreakpoints';
import { c } from '../../../utils/tailwind-merge';
import { EmptyState } from '../../molecules/EmptyState';
import {
  ProductListHeader,
  ProductListSeeMoreLink,
} from '../ProductListHeader';
import { ProductListInfo } from '../ProductListInfo';
import type { ProductListingProps } from './ProductListing.types';

export const ProductListing = <T,>({
  className = '',
  name,
  href,
  info,
  products,
  renderCard,
  microcopy,
}: ProductListingProps<T>) => {
  const isEmpty = products.length === 0;
  const breakpoints = useBreakpoints();
  const MAX_CARDS_MOBILE_TABLET = 4;

  const displayedProducts =
    breakpoints === null || breakpoints.isDesktop
      ? products
      : products.slice(0, MAX_CARDS_MOBILE_TABLET);

  return (
    <section
      className={c('flex flex-col gap-4 lg:gap-5', className)}
      data-test="product-listing"
    >
      <div>
        <ProductListHeader
          type="seeMore"
          size="lg"
          name={name}
          href={href}
          microcopy={{
            seeMore: microcopy.seeMore,
            seeMoreAria: microcopy.seeMoreAria,
          }}
        />
        <ProductListInfo {...info} microcopy={microcopy.info} />
      </div>
      {isEmpty ? (
        <>
          <EmptyState
            microcopy={microcopy.emptyState}
            titleClassName="hidden md:block"
            descriptionClassName="hidden md:block"
            iconSizeMobile={165}
            className="p-0"
          />
          <ProductListSeeMoreLink
            href={href}
            name={name}
            microcopy={{
              seeMore: microcopy.seeMore,
              seeMoreAria: microcopy.seeMoreAria,
            }}
            className="ml-1.5 md:hidden"
          />
        </>
      ) : (
        <>
          <div
            className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5 lg:gap-5"
            data-test="product-listing-grid"
          >
            {displayedProducts.map((product, index) =>
              renderCard(
                {
                  ...product,
                  className: c(
                    'md:min-w-auto !min-w-0',
                    (product as { className?: string }).className
                  ),
                } as T,
                index
              )
            )}
          </div>
          <ProductListSeeMoreLink
            href={href}
            name={name}
            microcopy={{
              seeMore: microcopy.seeMore,
              seeMoreAria: microcopy.seeMoreAria,
            }}
            className="ml-1.5 md:hidden"
          />
        </>
      )}
    </section>
  );
};

ProductListing.displayName = 'ProductListing';
